import { makeAutoObservable } from 'mobx'
import { KnowledgeBaseStatus } from 'entities/KnowledgeBase'
import { formatWidgetMessage } from 'pages/chatbot/ui/UnifiedAgent/lib/formatWidgetMessage'
import type { ChatStore } from 'pages/chatbot/ui/UnifiedAgent/ui/Chat/store/chatStore'
import type { KnowledgeBaseSectionStore } from './knowledgeBaseSectionStore'

const TOTAL_STEPS = 2

export const STEP_HEADERS = [
  'Should this agent use knowledge base files?',
  'Which files should the agent use?',
]

export const KB_ENABLE_OPTIONS = [
  { id: 'yes', label: 'Yes' },
  { id: 'no', label: 'No' },
]

export class KnowledgeBaseWidgetStore {
  step = 1
  focusedIndex = 0
  isSkipped = false
  localUseKnowledgeBase = false
  localDocuments: number[]

  constructor(readonly sectionStore: KnowledgeBaseSectionStore, readonly chatStore: ChatStore) {
    this.localDocuments = [...sectionStore.documents]
    makeAutoObservable(this)
  }

  get totalSteps() {
    if (this.step === 1) return 1
    return TOTAL_STEPS
  }

  get isLastStep() {
    return this.step === TOTAL_STEPS
  }

  get currentListLength() {
    return this.step === 1 ? KB_ENABLE_OPTIONS.length : 0
  }

  get safeIndex() {
    if (!this.currentListLength) return 0
    return Math.min(this.focusedIndex, this.currentListLength - 1)
  }

  get submitMessage() {
    if (this.isSkipped) {
      return formatWidgetMessage(STEP_HEADERS.map((header) => ({ q: header, a: 'Skip' })))
    }

    const { agentDocumentsStore } = this.sectionStore.agentKnowledgeBaseStore
    const selectedDocs = agentDocumentsStore.documents.filter((doc) =>
      agentDocumentsStore.selectedDocumentIds.includes(doc.id)
    )

    const filesAnswer =
      selectedDocs.length === 1 ? selectedDocs[0].name : `${selectedDocs.length} items`

    return formatWidgetMessage([
      { q: STEP_HEADERS[0], a: 'Yes' },
      { q: STEP_HEADERS[1], a: filesAnswer },
    ])
  }

  get isSubmitDisabled() {
    const { knowledgeBase, agentDocumentsStore } = this.sectionStore.agentKnowledgeBaseStore

    const isEmpty =
      knowledgeBase?.documentsCount === 0 && knowledgeBase.status !== KnowledgeBaseStatus.Processing

    return (
      isEmpty ||
      !!knowledgeBase?.isProcessingStatus ||
      agentDocumentsStore.isDocumentsLoading ||
      agentDocumentsStore.selectedDocumentIds.length === 0
    )
  }

  setFocusedIndex = (index: number) => {
    this.focusedIndex = index
  }

  moveFocus = (delta: number) => {
    if (!this.currentListLength) return
    this.focusedIndex = Math.min(Math.max(this.safeIndex + delta, 0), this.currentListLength - 1)
  }

  goBack = () => {
    this.step = Math.max(1, this.step - 1)
    this.focusedIndex = 0
  }

  setLocalUseKnowledgeBase = (value: boolean) => {
    this.localUseKnowledgeBase = value
  }

  setLocalDocuments = (ids: number[]) => {
    this.localDocuments = ids
  }

  reset = () => {
    this.step = 1
    this.focusedIndex = 0
    this.localUseKnowledgeBase = false
    this.localDocuments = []
  }

  handleSelect = () => {
    if (this.step !== 1) return
    const option = KB_ENABLE_OPTIONS[this.safeIndex]
    if (option.id === 'no') {
      const message = formatWidgetMessage([{ q: STEP_HEADERS[0], a: 'No' }])

      this.chatStore.setLocalWidgetState({ settings: { use_knowledge_base: false } })
      this.chatStore.sendMessage(message, () => {
        this.sectionStore.setUseKnowledgeBase(false)
        this.sectionStore.init()
      })
    } else {
      this.localUseKnowledgeBase = true
      this.step++
      this.focusedIndex = 0
    }
  }

  selectAt = (index: number) => {
    this.focusedIndex = index
    this.handleSelect()
  }

  handleSubmit = () => {
    const { agentDocumentsStore } = this.sectionStore.agentKnowledgeBaseStore
    const selectedDocumentIds = [...agentDocumentsStore.selectedDocumentIds]
    const selectedIds = new Set(selectedDocumentIds)
    const useLiveDocuments = agentDocumentsStore.documents.some(
      (doc) => selectedIds.has(doc.id) && doc.getIsLiveDocument()
    )
    const message = this.submitMessage

    this.chatStore.setLocalWidgetState({
      settings: { use_knowledge_base: true, use_live_documents: useLiveDocuments },
    })
    this.chatStore.sendMessage(message, () => {
      this.sectionStore.setKnowledgeBaseEnabled(true)
      this.sectionStore.setDocuments(selectedDocumentIds)
      this.sectionStore.init()
    })
  }

  handleClose = () => {
    this.isSkipped = true
    const message = this.submitMessage
    this.chatStore.sendMessage(message)
  }
}
